import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Inbox, Plus } from "lucide-react";

import { useEngagements } from "../../hooks/useEngagements";
import { StateBadge } from "../../components/StateBadge";
import { PageHeader } from "../../components/ui/PageHeader";
import { Timestamp } from "../../components/ui/Timestamp";
import { createEngagement, deleteEngagement } from "../../api/engagements";
import type { EngagementState, AutonomyTier, Channel, EmailConfigCreateBody } from "../../api/engagements";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ApiError } from "../../lib/api";
import type { StateBadgeKind } from "../../components/StateBadge";
import {
  ENGAGEMENT_CHANNELS,
  LIVE_CHANNELS,
  type EngagementChannelKey,
} from "../../lib/engagementChannels";
import { useIsMobile } from "../../lib/useMediaQuery";
import { fetchSendingProfiles } from "../../api/sendingProfiles";
import { MERGE_FIELDS } from "../../lib/mergeFields";

// Single-tenant default customer — mirrors the campaign create flow.
const DEFAULT_CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

type StateFilter = "ALL" | "draft" | "active" | "concluded";

function stateToBadgeKind(state: EngagementState): StateBadgeKind {
  switch (state) {
    case "active":               return "active";
    case "draft":                return "draft";
    case "paused":               return "paused";
    case "pending_authorization":return "info";
    case "concluded":            return "concluded";
    case "kill_switch_locked":   return "error";
    case "revoked":              return "error";
  }
}

function stateLabel(state: EngagementState): string {
  switch (state) {
    case "active":                return "ACTIVE";
    case "draft":                 return "DRAFT";
    case "paused":                return "PAUSED";
    case "pending_authorization": return "PENDING AUTH";
    case "concluded":             return "CONCLUDED";
    case "kill_switch_locked":    return "KILL SWITCH";
    case "revoked":               return "REVOKED";
  }
}

function tierLabel(tier: AutonomyTier): string {
  switch (tier) {
    case "tier_1": return "T1";
    case "tier_2": return "T2";
    case "tier_3": return "T3";
  }
}

/** Channel (engagement type) chip — icon + label from the channel registry. */
function ChannelCell({ channel }: { channel: string }) {
  const def = ENGAGEMENT_CHANNELS.find((c) => c.key === channel);
  if (!def) {
    return <span style={styles.tier}>{channel}</span>;
  }
  const Icon = def.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.04em",
        color: "var(--text-secondary)",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} strokeWidth={1.5} />
      {def.label}
    </span>
  );
}

export function EngagementsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useEngagements();
  const [filter, setFilter] = useState<StateFilter>("ALL");

  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTier, setNewTier] = useState<AutonomyTier>("tier_1");
  const [createError, setCreateError] = useState<string | null>(null);

  const [confirmEng, setConfirmEng] = useState<{ id: string; name: string } | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEngagement(id),
    onSuccess: () => {
      setConfirmEng(null);
      void qc.invalidateQueries({ queryKey: ["engagements"] });
    },
    onError: (err) =>
      alert(
        err instanceof ApiError && err.status === 409
          ? "This engagement can't be deleted while it's active, paused, locked, or pending authorization."
          : err instanceof ApiError
            ? err.message
            : "Failed to delete engagement.",
      ),
  });

  const createMut = useMutation({
    mutationFn: (args: { channel: Channel; emailConfig?: EmailConfigCreateBody }) =>
      createEngagement({
        customer_id: DEFAULT_CUSTOMER_ID,
        display_name: newName.trim(),
        autonomy_tier: newTier,
        channel: args.channel,
        ...(args.emailConfig ? { email_config: args.emailConfig } : {}),
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["engagements"] });
      setShowCreate(false);
      setNewName("");
      setNewTier("tier_1");
      setCreateError(null);
      navigate(`/engagements/${res.record.engagement_id}`);
    },
    onError: (err) =>
      setCreateError(err instanceof Error ? err.message : "Failed to create engagement."),
  });

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    if (filter === "ALL") return items;
    return items.filter((it) => it.state === filter);
  }, [data, filter]);

  const total = data?.total ?? 0;
  const active = (data?.items ?? []).filter((i) => i.state === "active").length;
  const isMobile = useIsMobile();

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow="Operator console"
        title="Engagement registry"
        subtitle="Authoring, authorization, and lifecycle control for every authorized red-team engagement."
        meta={
          <span>
            <span style={{ color: "var(--text-secondary)" }}>{total}</span> total
            <Dot />
            <span style={{ color: "var(--accent)" }}>{active}</span> active
            <Dot />
            <span style={{ color: "var(--text-muted)" }}>{filter !== "ALL" ? `filter: ${filter}` : "all states"}</span>
          </span>
        }
        actions={
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <FilterStrip value={filter} onChange={setFilter} />
            <button
              type="button"
              onClick={() => { setShowCreate((v) => !v); setCreateError(null); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-input)",
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={14} strokeWidth={2} /> New engagement
            </button>
          </div>
        }
      />

      {showCreate && (
        <CreateForm
          name={newName}
          onName={(v) => { setNewName(v); setCreateError(null); }}
          tier={newTier}
          onTier={setNewTier}
          error={createError}
          pending={createMut.isPending}
          onSubmit={(args) => {
            if (!newName.trim()) return;
            createMut.mutate(args);
          }}
          onCancel={() => { setShowCreate(false); setNewName(""); setNewTier("tier_1"); setCreateError(null); }}
        />
      )}

      {isLoading ? (
        <StateMessage label="Loading engagements…" />
      ) : isError ? (
        <StateMessage label="Failed to load engagements." tone="error" />
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((item, idx) => (
            <div
              key={item.engagement_id}
              className="animate-in"
              onClick={() => navigate(`/engagements/${item.engagement_id}`)}
              style={{
                background: "var(--bg-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-card)",
                padding: 14,
                display: "flex",
                flexDirection: "column",
                gap: 8,
                cursor: "pointer",
                animationDelay: `${idx * 35}ms`,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <span style={styles.name}>{item.display_name}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <StateBadge kind={stateToBadgeKind(item.state)}>
                    {stateLabel(item.state)}
                  </StateBadge>
                  <button
                    type="button"
                    aria-label={`Delete ${item.display_name}`}
                    title="Delete engagement"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmEng({ id: item.engagement_id, name: item.display_name });
                    }}
                    style={{
                      display: "grid",
                      placeItems: "center",
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      border: "1px solid var(--border-subtle)",
                      background: "transparent",
                      color: "var(--error)",
                      cursor: "pointer",
                      fontSize: 13,
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={styles.idLine}>{item.engagement_id.slice(0, 8)}</span>
                  <ChannelCell channel={item.channel} />
                </div>
                <span style={styles.tier}>{tierLabel(item.autonomy_tier)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                <Timestamp iso={item.created_at} variant="stacked" align="left" />
                <ChevronRight size={14} strokeWidth={1.5} color="var(--text-muted)" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table>
          <THead>
            <Col width="1fr">Engagement</Col>
            <Col width="110px">Channel</Col>
            <Col width="150px">State</Col>
            <Col width="60px" align="right">Tier</Col>
            <Col width="160px" align="right">Created</Col>
            <Col width="36px" />
          </THead>
          {filtered.map((item, idx) => (
            <Row
              key={item.engagement_id}
              onClick={() => navigate(`/engagements/${item.engagement_id}`)}
              style={{ animationDelay: `${idx * 35}ms` }}
            >
              <Col width="1fr">
                <div style={styles.name}>{item.display_name}</div>
                <div style={styles.idLine}>{item.engagement_id}</div>
              </Col>
              <Col width="110px">
                <ChannelCell channel={item.channel} />
              </Col>
              <Col width="150px">
                <StateBadge kind={stateToBadgeKind(item.state)}>
                  {stateLabel(item.state)}
                </StateBadge>
              </Col>
              <Col width="60px" align="right">
                <span style={styles.tier}>{tierLabel(item.autonomy_tier)}</span>
              </Col>
              <Col width="160px" align="right">
                <Timestamp iso={item.created_at} variant="stacked" align="right" />
              </Col>
              <Col width="36px" align="right">
                <button
                  type="button"
                  aria-label={`Delete ${item.display_name}`}
                  title="Delete engagement"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmEng({ id: item.engagement_id, name: item.display_name });
                  }}
                  style={{
                    display: "grid",
                    placeItems: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid var(--border-subtle)",
                    background: "transparent",
                    color: "var(--error)",
                    cursor: "pointer",
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </Col>
            </Row>
          ))}
        </Table>
      )}

      <ConfirmDialog
        open={confirmEng !== null}
        title="Delete engagement?"
        body={
          <>
            Delete <strong>{confirmEng?.name}</strong>? Calls, recordings, and audit
            records from this engagement are preserved. This cannot be undone.
          </>
        }
        busy={deleteMut.isPending}
        onConfirm={() => confirmEng && deleteMut.mutate(confirmEng.id)}
        onCancel={() => setConfirmEng(null)}
      />
    </div>
  );
}

/* ---------- create form ---------- */

function CreateForm({
  name,
  onName,
  tier,
  onTier,
  error,
  pending,
  onSubmit,
  onCancel,
}: {
  name: string;
  onName: (v: string) => void;
  tier: AutonomyTier;
  onTier: (v: AutonomyTier) => void;
  error: string | null;
  pending: boolean;
  onSubmit: (args: { channel: Channel; emailConfig?: EmailConfigCreateBody }) => void;
  onCancel: () => void;
}) {
  const fieldLabel: React.CSSProperties = {
    fontSize: 9.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "var(--text-muted)",
    fontFamily: "var(--font-mono)",
  };
  const inputStyle: React.CSSProperties = {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-input)",
    padding: "8px 12px",
    color: "var(--text-primary)",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
    outline: "none",
  };
  const [channel, setChannel] = useState<EngagementChannelKey>("voice");
  const [plannedNote, setPlannedNote] = useState<string | null>(null);

  // Email-config draft (only used when channel === "email").
  const [sendingProfileId, setSendingProfileId] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [relayBaseUrl, setRelayBaseUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");

  const { data: profilesData } = useQuery({
    queryKey: ["sending-profiles"],
    queryFn: fetchSendingProfiles,
    enabled: channel === "email",
  });
  const sendingProfiles = profilesData?.items ?? [];

  const emailConfigValid =
    channel !== "email" ||
    (sendingProfileId &&
      subject.trim() &&
      bodyHtml.trim() &&
      relayBaseUrl.trim() &&
      webhookSecret.trim());

  function buildSubmitArgs(): { channel: Channel; emailConfig?: EmailConfigCreateBody } {
    if (channel === "email") {
      return {
        channel: "email",
        emailConfig: {
          sending_profile_id: sendingProfileId,
          subject: subject.trim(),
          body_html: bodyHtml,
          body_text: null,
          relay_base_url: relayBaseUrl.trim(),
          webhook_bearer_secret: webhookSecret,
        },
      };
    }
    if (channel === "video") {
      // Video config (avatar/voice/script) is built on the Detail page, not at
      // create — a video engagement starts as a bare DRAFT.
      return { channel: "video" };
    }
    return { channel: "voice" };
  }

  return (
    <div
      className="animate-in"
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-card)",
        padding: 18,
        marginBottom: 20,
        display: "flex",
        gap: 14,
        alignItems: "flex-end",
        flexWrap: "wrap",
      }}
    >
      {/* Channel picker — voice is live; the rest are scaffolded placeholders. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexBasis: "100%" }}>
        <span style={fieldLabel}>Channel</span>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {ENGAGEMENT_CHANNELS.map((ch) => {
            const live = LIVE_CHANNELS.has(ch.key);
            const selected = channel === ch.key;
            const Icon = ch.icon;
            return (
              <button
                key={ch.key}
                type="button"
                title={live ? ch.blurb : `${ch.blurb} — ${ch.scaffoldNote}`}
                onClick={() => {
                  if (live) {
                    setChannel(ch.key);
                    setPlannedNote(null);
                  } else {
                    setPlannedNote(`${ch.label} — ${ch.scaffoldNote}`);
                  }
                }}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 4,
                  width: 132,
                  padding: "10px 12px",
                  borderRadius: "var(--radius-input)",
                  border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
                  background: selected ? "var(--accent-subtle)" : "var(--bg-input)",
                  color: live ? "var(--text-primary)" : "var(--text-muted)",
                  cursor: live ? "pointer" : "not-allowed",
                  opacity: live ? 1 : 0.7,
                  textAlign: "left",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Icon size={14} strokeWidth={1.75} />
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{ch.label}</span>
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 8.5,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    padding: "1px 6px",
                    borderRadius: 3,
                    background: live ? "var(--accent)" : "var(--bg-tertiary)",
                    color: live ? "#04181A" : "var(--text-muted)",
                    border: live ? "none" : "1px solid var(--border-subtle)",
                  }}
                >
                  {live ? "LIVE" : "SOON"}
                </span>
              </button>
            );
          })}
        </div>
        {plannedNote && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-sans)", lineHeight: 1.5, marginTop: 2 }}>
            {plannedNote} <span style={{ color: "var(--text-muted)", opacity: 0.8 }}>(backend not wired yet — voice only for now)</span>
          </div>
        )}
      </div>

      {channel === "email" && (
        <div style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={fieldLabel}>Sending profile</span>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <select
                value={sendingProfileId}
                onChange={(e) => setSendingProfileId(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer", minWidth: 240 }}
              >
                <option value="">Select a sending profile…</option>
                {sendingProfiles.map((p) => (
                  <option key={p.sending_profile_id} value={p.sending_profile_id}>
                    {p.name} ({p.from_address}){p.has_credentials ? "" : " — no creds"}
                  </option>
                ))}
              </select>
              <Link
                to="/sending-profiles"
                style={{ fontSize: 11.5, color: "var(--accent)", textDecoration: "underline" }}
              >
                Manage sending profiles
              </Link>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={fieldLabel}>Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Action required: verify your account"
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={fieldLabel}>Body (HTML)</span>
            <textarea
              value={bodyHtml}
              onChange={(e) => setBodyHtml(e.target.value)}
              placeholder="<p>Hi {{first_name}}, please review …</p>"
              rows={6}
              style={{ ...inputStyle, fontFamily: "var(--font-mono)", resize: "vertical" }}
            />
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
              <span style={{ fontSize: 10.5, color: "var(--text-muted)", marginRight: 4 }}>
                Merge fields:
              </span>
              {MERGE_FIELDS.map((f) => (
                <button
                  key={f.token}
                  type="button"
                  title={f.description}
                  onClick={() => setBodyHtml((b) => b + f.token)}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    padding: "2px 7px",
                    borderRadius: 4,
                    border: "1px solid var(--border)",
                    background: "var(--bg-input)",
                    color: "var(--text-secondary)",
                    cursor: "pointer",
                  }}
                >
                  {f.token}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 220 }}>
              <span style={fieldLabel}>Relay base URL</span>
              <input
                value={relayBaseUrl}
                onChange={(e) => setRelayBaseUrl(e.target.value)}
                placeholder="https://relay.your-infra.example"
                style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 220 }}>
              <span style={fieldLabel}>Webhook bearer secret</span>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="shared secret for the results webhook"
                autoComplete="new-password"
                style={{ ...inputStyle, fontFamily: "var(--font-mono)" }}
              />
            </div>
          </div>
          <div style={{ fontSize: 10.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
            The lure link ({"{{lure_link}}"}) resolves to <code style={{ fontFamily: "var(--font-mono)" }}>relay base + /c/ + token</code>.
            The bearer secret authenticates your relay&apos;s POSTs to the results webhook — it is write-only and never shown again.
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1, minWidth: 220 }}>
        <span style={fieldLabel}>Display name</span>
        <input
          autoFocus
          value={name}
          onChange={(e) => onName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && name.trim() && emailConfigValid && !pending) onSubmit(buildSubmitArgs()); }}
          placeholder="e.g. Acme Q2 — IT help desk pretext"
          style={inputStyle}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 160 }}>
        <span style={fieldLabel}>Autonomy tier</span>
        <select
          value={tier}
          onChange={(e) => onTier(e.target.value as AutonomyTier)}
          style={{ ...inputStyle, cursor: "pointer" }}
        >
          <option value="tier_1">Tier 1 — supervised</option>
          <option value="tier_2">Tier 2 — semi-autonomous</option>
          <option value="tier_3">Tier 3 — autonomous</option>
        </select>
      </div>
      <button
        type="button"
        onClick={() => onSubmit(buildSubmitArgs())}
        disabled={!name.trim() || !emailConfigValid || pending}
        style={{
          background: !name.trim() || !emailConfigValid ? "var(--bg-tertiary)" : "var(--accent)",
          color: !name.trim() || !emailConfigValid ? "var(--text-muted)" : "#fff",
          border: "1px solid var(--accent)",
          borderRadius: "var(--radius-input)",
          padding: "8px 18px",
          fontSize: 13,
          fontWeight: 600,
          cursor: !name.trim() || !emailConfigValid ? "not-allowed" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {pending ? "Creating…" : "Create"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        style={{
          background: "transparent",
          color: "var(--text-secondary)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-input)",
          padding: "8px 16px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Cancel
      </button>
      {error && (
        <div style={{ flexBasis: "100%", color: "var(--error)", fontSize: 12, fontFamily: "var(--font-mono)" }}>
          {error}
        </div>
      )}
      <div style={{ flexBasis: "100%", color: "var(--text-muted)", fontSize: 11.5 }}>
        {channel === "email"
          ? "Creates a DRAFT email engagement. Set the email allowlist + sign the ROE on the detail page, then activate."
          : "Creates a DRAFT template engagement. Bind a persona and configure it on the detail page, then activate."}
      </div>
    </div>
  );
}

/* ---------- atoms ---------- */

function Dot() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 3,
        height: 3,
        background: "var(--text-muted)",
        margin: "0 10px 2px",
        verticalAlign: "middle",
      }}
    />
  );
}

function FilterStrip({
  value,
  onChange,
}: {
  value: StateFilter;
  onChange: (v: StateFilter) => void;
}) {
  const options: StateFilter[] = ["ALL", "draft", "active", "concluded"];
  return (
    <div
      style={{
        display: "inline-flex",
        background: "var(--bg-secondary)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-input)",
        overflow: "hidden",
        fontFamily: "var(--font-mono)",
        fontSize: 10,
      }}
    >
      {options.map((opt, i) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              padding: "7px 14px",
              border: "none",
              borderLeft: i === 0 ? "none" : "1px solid var(--border-subtle)",
              background: active ? "var(--accent-subtle)" : "transparent",
              color: active ? "var(--accent)" : "var(--text-muted)",
              fontFamily: "inherit",
              fontSize: "inherit",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              fontWeight: active ? 600 : 500,
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function Table({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        overflow: "hidden",
      }}
    >
      {children}
    </div>
  );
}

function THead({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: cols(children),
        gap: 16,
        padding: "10px 18px",
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: "0.16em",
        color: "var(--text-muted)",
      }}
    >
      {children}
    </div>
  );
}

function Row({
  children,
  onClick,
  style,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  style?: React.CSSProperties;
}) {
  return (
    <div
      onClick={onClick}
      className="animate-in"
      style={{
        display: "grid",
        gridTemplateColumns: cols(children),
        gap: 16,
        padding: "14px 18px",
        borderBottom: "1px solid var(--border-subtle)",
        cursor: onClick ? "pointer" : "default",
        alignItems: "center",
        ...style,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "var(--bg-hover)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = "transparent";
      }}
    >
      {children}
    </div>
  );
}

function Col({
  width,
  align = "left",
  children,
}: {
  width: string;
  align?: "left" | "right";
  children?: React.ReactNode;
}) {
  return (
    <div
      data-col-width={width}
      style={{
        textAlign: align,
        display: "flex",
        flexDirection: "column",
        alignItems: align === "right" ? "flex-end" : "stretch",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      {children}
    </div>
  );
}

function cols(children: React.ReactNode): string {
  // Pull widths out of the Col children to build the grid-template-columns
  const widths: string[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const arr = (Array.isArray(children) ? children : [children]) as any[];
  for (const child of arr) {
    if (child && child.props && child.props.width) widths.push(child.props.width);
  }
  return widths.join(" ");
}

function StateMessage({ label, tone }: { label: string; tone?: "error" }) {
  return (
    <div
      style={{
        padding: "48px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: "64px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px dashed var(--border)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <Inbox
        size={36}
        strokeWidth={1.25}
        color="var(--text-muted)"
        style={{ marginBottom: 12 }}
      />
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 18,
          color: "var(--text-secondary)",
        }}
      >
        No engagements
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 12,
          color: "var(--text-muted)",
        }}
      >
        Author one to begin authorized red-team operations.
      </div>
    </div>
  );
}

const styles = {
  name: {
    fontSize: 13.5,
    fontWeight: 600,
    color: "var(--text-primary)",
    letterSpacing: "-0.005em",
  } as const,
  idLine: {
    fontSize: 10.5,
    color: "var(--text-muted)",
    fontFamily: "var(--font-mono)",
    marginTop: 3,
    letterSpacing: "0.04em",
  } as const,
  tier: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--text-secondary)",
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border-subtle)",
    padding: "2px 8px",
    borderRadius: 2,
    letterSpacing: "0.10em",
  } as const,
  time: {
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    color: "var(--text-muted)",
  } as const,
};
